// Funções de derivação para o painel de Pesagem Individual por Corral/Baia.
// Portadas das fórmulas aprovadas em `pagina_template.html` (ver
// integracao-vuejs-pesagem-por-corral.md, seção 2). Não recalcular no backend.

const pick = (obj, ...keys) => {
  for (const k of keys) {
    if (obj[k] !== undefined) return obj[k]
  }
  return undefined
}

/**
 * O backend passou a devolver o payload com chaves em inglês em 18/09/2026
 * (ver pesagem-individual/HANDOFF.md, seção 5) — os valores de enum
 * (status/side/phase) continuam em espanhol, só as chaves mudaram. Esta
 * função normaliza para o formato interno em espanhol que o resto do app
 * consome, aceitando as duas variantes (inglês novo e espanhol antigo) para
 * não quebrar de novo se o contrato mudar outra vez ou voltar atrás.
 */
export function normalizeIndividualWeighingPayload(raw) {
  if (!raw || typeof raw !== 'object') return raw

  const grupos = (pick(raw, 'groups', 'grupos') || []).map(g => ({
    grupo: pick(g, 'group', 'grupo'),
    estado: pick(g, 'status', 'estado'),
    fecha: pick(g, 'date', 'fecha'),
    animales: pick(g, 'animals', 'animales') ?? null,
    peso_medio: pick(g, 'avg_weight_kg', 'peso_medio') ?? null,
    edad: pick(g, 'age', 'edad') ?? null,
    camaras: pick(g, 'cameras', 'camaras') || [],
    corrales: pick(g, 'pens', 'corrales') || [],
  }))

  const camaras = (pick(raw, 'cameras', 'camaras') || []).map(cm => {
    const lados = pick(cm, 'sides', 'lados') || {}
    return {
      camara: pick(cm, 'camera', 'camara'),
      grupo: pick(cm, 'group', 'grupo') ?? null,
      lados: {
        izquierdo: pick(lados, 'left', 'izquierdo') ?? null,
        derecho: pick(lados, 'right', 'derecho') ?? null,
      },
    }
  })

  const corrales = (pick(raw, 'pens', 'corrales') || []).map(c => ({
    corral: pick(c, 'pen', 'corral'),
    grupo: pick(c, 'group', 'grupo'),
    camara: pick(c, 'camera', 'camara'),
    lado: pick(c, 'side', 'lado'),
    sexo: pick(c, 'sex', 'sexo'),
    genetica: pick(c, 'genetics', 'genetica'),
    cantidad: pick(c, 'quantity', 'cantidad'),
    peso_ingreso: pick(c, 'entry_weight_kg', 'peso_ingreso'),
    fecha_ingreso: pick(c, 'entry_date', 'fecha_ingreso'),
    edad_ingreso: pick(c, 'entry_age', 'edad_ingreso'),
    camera_mac: pick(c, 'camera_mac') ?? null,
  }))

  const serie = (pick(raw, 'series', 'serie') || []).map(r => ({
    corral: pick(r, 'pen', 'corral'),
    fecha: pick(r, 'date', 'fecha'),
    dia_lote: pick(r, 'lot_day', 'dia_lote'),
    edad: pick(r, 'age', 'edad'),
    peso_kg: pick(r, 'weight_s4_kg', 'weight_pred_kg', 'weight_kg', 'peso_kg'),
    p10: r.p10,
    p90: r.p90,
    lecturas: pick(r, 'readings', 'lecturas') ?? null,
    fase: pick(r, 'phase', 'fase'),
    pic_kg: r.pic_kg,
  }))

  return {
    granja: pick(raw, 'farm', 'granja'),
    galpon: pick(raw, 'barn', 'galpon'),
    empresa: pick(raw, 'company', 'empresa'),
    fecha_referencia: pick(raw, 'reference_date', 'fecha_referencia'),
    peso_objetivo_kg: pick(raw, 'target_weight_kg', 'peso_objetivo_kg'),
    ejemplo: pick(raw, 'example', 'ejemplo') === true,
    grupos,
    camaras,
    corrales,
    serie,
  }
}

export const MES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"]

export function fmt(n, d = 1) {
  if (n == null || isNaN(n)) return "—"
  return n.toLocaleString("es-ES", { minimumFractionDigits: d, maximumFractionDigits: d })
}

export function fdate(s) {
  if (!s) return "—"
  const [, m, d] = s.split("-").map(Number)
  return `${d} ${MES[m - 1]}`
}

export function fdateL(s) {
  if (!s) return "—"
  const [y, m, d] = s.split("-").map(Number)
  return `${d} ${MES[m - 1]} ${y}`
}

export function addDays(s, n) {
  const [y, m, d] = s.split("-").map(Number)
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10)
}

export function pct(x) {
  if (x == null || isNaN(x)) return "—"
  const sign = x > 0 ? "+" : x < 0 ? "−" : ""
  return sign + fmt(Math.abs(x) * 100, 1) + "%"
}

// sem cor abaixo de 2%, satura em 20%
export function favClass(x) {
  if (x == null || isNaN(x) || Math.abs(x) < 0.02) return "neutral"
  return x > 0 ? "good" : "bad"
}

export function favIntensity(x) {
  if (x == null || isNaN(x)) return 0
  const a = Math.abs(x)
  if (a < 0.02) return 0
  return Math.min(1, (a - 0.02) / 0.18)
}

export function lectState(l) {
  if (l == null) return ["none", "sin datos"]
  if (l >= 300) return ["ok", "lectura normal"]
  if (l >= 150) return ["warn", "atención"]
  return ["bad", "revisar cámara"]
}

function sortByDiaLote(rows) {
  return [...rows].sort((a, b) => a.dia_lote - b.dia_lote)
}

function daysBetween(start, end) {
  const ms = Date.parse(end) - Date.parse(start)
  if (!Number.isFinite(ms) || ms === 0) return null
  return ms / 86400000
}

const PEN_WEIGHT_FLAGS = {
  low_photos: "poucas fotos",
  missing_features: "sem características",
  crazy_jump: "salto inconsistente",
}

export function penWeightFlagNote(flag) {
  if (!flag) return ""
  const label = PEN_WEIGHT_FLAGS[flag]
  if (!label) return "Neste dia a curva seguiu a referência."
  return `Neste dia a curva seguiu a referência (${label}).`
}

function groupKey(name) {
  const match = String(name || "").match(/(\d+)\s*$/)
  return match ? Number(match[1]) : (name || "—")
}

function presentNumbers(values) {
  return values.filter(value => value != null && !Number.isNaN(Number(value))).map(value => Number(value))
}

function sameOrSum(values) {
  const present = presentNumbers(values)
  if (!present.length) return null
  const unique = [...new Set(present)]
  if (unique.length === 1) return unique[0]
  return present.reduce((sum, value) => sum + value, 0)
}

function sameOrMean(values) {
  const present = presentNumbers(values)
  if (!present.length) return null
  const unique = [...new Set(present)]
  if (unique.length === 1) return unique[0]
  return present.reduce((sum, value) => sum + value, 0) / present.length
}

function readPenDays(pen) {
  const candidates = [pen?.days, pen?.weights, pen?.series]
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate
    if (candidate && typeof candidate === 'object') {
      return Object.entries(candidate).map(([key, value]) => {
        if (value && typeof value === 'object') {
          return { day_date: value.day_date ?? value.date ?? key, ...value }
        }
        return { day_date: key, weight_corrected_kg: value }
      })
    }
  }
  return []
}

function readDayPoint(day) {
  if (!day || typeof day !== 'object') return null
  const fecha = day.day_date ?? day.date ?? day.fecha
  const peso = day.weight_s4_kg ?? day.weight_corrected_kg ?? day.weight_pred_kg ?? day.weight_kg ?? day.peso_kg
  if (fecha == null || peso == null || peso === '') return null
  const pesoKg = Number(peso)
  if (Number.isNaN(pesoKg)) return null
  return {
    fecha: String(fecha).slice(0, 10),
    peso_kg: pesoKg,
    p10: day.weight_pred_kg_p10 == null ? null : Number(day.weight_pred_kg_p10),
    p90: day.weight_pred_kg_p90 == null ? null : Number(day.weight_pred_kg_p90),
    ref_kg: day.ref_kg == null ? null : Number(day.ref_kg),
    lecturas: day.photos_used_count ?? day.photos ?? null,
    flag: day.flag || null,
  }
}

export function seriesRowsFromPens(pens, fallbackPen) {
  const serie = []
  for (const pen of pens || []) {
    if (!pen || pen.confirmed === false) continue
    readPenDays(pen).forEach((day, index) => {
      const point = readDayPoint(day)
      if (!point) return
      serie.push({
        corral: pen.pen ?? fallbackPen,
        dia_lote: index + 1,
        edad: null,
        fase: 'observado',
        pic_kg: null,
        ...point,
      })
    })
  }
  return serie
}

export function pensFromPenWeightsPayload(payload) {
  if (Array.isArray(payload?.pens)) return payload.pens
  if (payload && (payload.pen != null || Array.isArray(payload.days))) return [payload]
  return []
}

export function mergePenWeightSeries(current, payload, pen) {
  if (!current) return current
  const pens = pensFromPenWeightsPayload(payload)
    .filter(item => String(item.pen ?? pen) === String(pen))
  const serie = seriesRowsFromPens(pens, pen)
  if (!serie.length) return current
  const latest = serie.reduce((value, row) => (
    !value || row.fecha > value ? row.fecha : value
  ), null)
  const runDate = payload?.run?.created_at
    ? String(payload.run.created_at).slice(0, 10)
    : null
  return {
    ...current,
    fecha_referencia: runDate || latest || current.fecha_referencia,
    serie: [
      ...(current.serie || []).filter(row => String(row.corral) !== String(pen)),
      ...serie,
    ],
  }
}

/**
 * Converte GET /api/batches/{lote}/pen-weights no formato interno da página.
 * Não projeta até o peso alvo e usa weight_s4_kg como peso de referência
 * (fallback para weight_corrected_kg, depois weight_pred_kg quando ausente);
 * não usa weight_true_kg nem weight_pred_kg_raw.
 */
export function mapPenWeightsResponse(payload, labels = {}) {
  const pens = Array.isArray(payload?.pens) ? [...payload.pens] : []
  pens.sort((a, b) => Number(a.pen) - Number(b.pen))

  const corrales = pens.map(pen => ({
    corral: pen.pen,
    grupo: groupKey(pen.group),
    camara: pen.camera_id,
    lado: pen.side === "right" ? "derecho" : "izquierdo",
    sexo: pen.sex === "F" ? "Fêmea" : pen.sex === "M" ? "Macho" : (pen.sex || "—"),
    genetica: null,
    cantidad: pen.heads ?? null,
    peso_ingreso: pen.w0_kg ?? null,
    fecha_ingreso: pen.entry_date || null,
    edad_ingreso: pen.age_at_entry_days ?? null,
    confirmed: pen.confirmed !== false,
  }))

  const serie = seriesRowsFromPens(pens)

  const groups = new Map()
  for (const pen of pens) {
    const key = pen.group || "—"
    if (!groups.has(key)) {
      groups.set(key, {
        grupo: groupKey(pen.group),
        estado: "alojado",
        fecha: null,
        animales: null,
        peso_medio: null,
        edad: null,
        camaras: [],
        corrales: [],
      })
    }
    const group = groups.get(key)
    group.corrales.push(pen.pen)
    if (pen.camera_id != null && !group.camaras.includes(pen.camera_id)) {
      group.camaras.push(pen.camera_id)
    }
  }

  for (const [key, group] of groups) {
    const members = pens.filter(pen => (pen.group || "—") === key)
    const confirmed = members.length > 0 && members.every(pen => pen.confirmed !== false)
    group.estado = confirmed ? "alojado" : "nao_confirmado"
    group.fecha = members.find(pen => pen.entry_date)?.entry_date || null
    group.animales = sameOrSum(members.map(pen => pen.heads))
    group.peso_medio = sameOrMean(members.map(pen => pen.w0_kg))
    group.edad = members.find(pen => pen.age_at_entry_days != null)?.age_at_entry_days ?? null
    group.camaras.sort((a, b) => Number(a) - Number(b))
  }

  const cameras = new Map()
  for (const pen of pens) {
    if (pen.camera_id == null) continue
    if (!cameras.has(pen.camera_id)) {
      cameras.set(pen.camera_id, {
        camara: pen.camera_id,
        grupo: groupKey(pen.group),
        lados: { izquierdo: null, derecho: null },
      })
    }
    const side = pen.side === "right" ? "derecho" : "izquierdo"
    cameras.get(pen.camera_id).lados[side] = pen.pen
  }

  const latestDay = serie.reduce((latest, row) => (
    !latest || row.fecha > latest ? row.fecha : latest
  ), null)
  const runDate = payload?.run?.created_at
    ? String(payload.run.created_at).slice(0, 10)
    : latestDay

  return {
    granja: labels.granja || "",
    galpon: labels.galpon || "",
    empresa: labels.empresa || "",
    fecha_referencia: runDate,
    peso_objetivo_kg: null,
    ejemplo: false,
    penWeights: true,
    grupos: [...groups.values()].sort((a, b) => Number(a.grupo) - Number(b.grupo)),
    camaras: [...cameras.values()].sort((a, b) => Number(a.camara) - Number(b.camara)),
    corrales,
    serie,
  }
}

/** Deriva métricas por corral a partir do JSON bruto (contrato GET /pesagem-corral). */
export function deriveCorrales(raw) {
  const serieBy = {}
  for (const r of raw.serie || []) {
    (serieBy[r.corral] = serieBy[r.corral] || []).push(r)
  }
  for (const k in serieBy) serieBy[k] = sortByDiaLote(serieBy[k])

  return (raw.corrales || []).map(c => {
    const s = serieBy[c.corral] || []
    const obs = s.filter(r => r.fase === "observado")
    if (c.confirmed === false) {
      return {
        ...c,
        s: [],
        obs: [],
        last: null,
        actual: null,
        gmd: null,
        vspic: null,
        peso_referencia_idade: null,
        dias_obj: null,
        fecha_obj: null,
        idade: null,
        dias_alojado: null,
        lect: null,
        lectClass: "none",
        lectLabel: "entrada ainda não confirmada",
        estado: "entrada ainda não confirmada",
      }
    }

    const last = obs.length ? obs[obs.length - 1] : null
    const base = obs.length >= 8 ? obs[obs.length - 8] : obs[0]
    const gmd = last && base && last.dia_lote !== base.dia_lote
      ? (last.peso_kg - base.peso_kg) / (last.dia_lote - base.dia_lote)
      : null
    const vspic = last && last.pic_kg ? last.peso_kg / last.pic_kg - 1 : null
    const idade = last?.edad ?? (c.edad_ingreso != null && last ? c.edad_ingreso + (last.dia_lote - 1) : null)
    const diasAlojado = last?.dia_lote ?? null
    const obj = raw.peso_objetivo_kg != null
      ? s.find(r => r.peso_kg >= raw.peso_objetivo_kg)
      : null
    const daySpan = raw.penWeights && last?.fecha && base?.fecha
      ? daysBetween(base.fecha, last.fecha)
      : (last && base ? last.dia_lote - base.dia_lote : null)
    const gmdByDate = last && base && daySpan
      ? (last.peso_kg - base.peso_kg) / daySpan
      : gmd
    let lectClass
    let lectLabel
    let estado
    if (raw.penWeights) {
      if (!last) {
        lectClass = "none"
        lectLabel = "sem pesagem"
        estado = "sem pesagem"
      } else if (last.flag) {
        lectClass = "warn"
        lectLabel = "seguiu a referência"
        estado = "referência"
      } else {
        lectClass = "ok"
        lectLabel = "ok"
        estado = "ok"
      }
    } else {
      [lectClass, lectLabel] = lectState(last?.lecturas)
      estado = !last ? "sin datos" : lectClass === "bad" ? "revisar cámara" : lectClass === "warn" ? "atención" : "ok"
    }
    return {
      ...c,
      s,
      obs,
      last,
      actual: last?.peso_kg ?? null,
      gmd: gmdByDate,
      vspic,
      peso_referencia_idade: last?.ref_kg ?? null,
      dias_obj: obj?.dia_lote ?? null,
      fecha_obj: obj?.fecha ?? null,
      idade,
      dias_alojado: diasAlojado,
      lect: last?.lecturas ?? null,
      lectClass,
      lectLabel,
      estado,
    }
  })
}

const latestPenWeight = (corral) => {
  const value = corral?.actual ?? corral?.peso_ingreso
  const number = Number(value)
  if (value == null || Number.isNaN(number)) return null
  return number
}

const entryPenWeight = (corral) => {
  const value = corral?.peso_ingreso
  const number = Number(value)
  if (value == null || Number.isNaN(number)) return null
  return number
}

const currentPenAge = (corral) => {
  const value = corral?.idade
  const number = Number(value)
  if (value == null || Number.isNaN(number)) return null
  return number
}

function weightedPenAverage(corrales, getWeight) {
  const pens = (corrales || []).filter(corral => getWeight(corral) != null)
  if (!pens.length) return null
  const byGroup = new Map()
  for (const pen of pens) {
    const key = pen.grupo ?? pen.corral
    if (!byGroup.has(key)) byGroup.set(key, [])
    byGroup.get(key).push(pen)
  }
  let weighted = 0
  let heads = 0
  for (const members of byGroup.values()) {
    const counts = presentNumbers(members.map(pen => pen.cantidad))
    const repeated = counts.length > 0 && new Set(counts).size === 1
    const groupHeads = repeated
      ? counts[0]
      : counts.reduce((sum, value) => sum + value, 0)
    const mean = repeated || !groupHeads
      ? members.reduce((sum, pen) => sum + getWeight(pen), 0) / members.length
      : members.reduce((sum, pen) => sum + getWeight(pen) * Number(pen.cantidad), 0) / groupHeads
    const share = groupHeads || members.length
    weighted += mean * share
    heads += share
  }
  return heads ? weighted / heads : null
}

export function averageLatestWeight(corrales) {
  return weightedPenAverage(corrales, latestPenWeight)
}

export function averageEntryWeight(corrales) {
  return weightedPenAverage(corrales, entryPenWeight)
}

export function averageCurrentAge(corrales) {
  return weightedPenAverage(corrales, currentPenAge)
}

/** KPIs do galpão: média da última pesagem de cada baia alojada, ponderada por cabeças. */
export function deriveKpisGalpon(raw, corrales) {
  const act = (corrales || []).filter(c => c.confirmed !== false && latestPenWeight(c) != null && Number(c.cantidad) > 0)
  const N = act.reduce((a, c) => a + Number(c.cantidad), 0)
  if (!N) return null
  const w = f => {
    const usable = act.filter(c => {
      const value = f(c)
      return value != null && !Number.isNaN(Number(value))
    })
    const weight = usable.reduce((a, c) => a + Number(c.cantidad), 0)
    if (!weight) return null
    return usable.reduce((a, c) => a + Number(f(c)) * Number(c.cantidad), 0) / weight
  }
  const pesoMedio = averageLatestWeight(act)
  const gmd = w(c => c.gmd)
  const vspic = w(c => c.vspic)
  const pesoIng = w(c => c.peso_ingreso)
  const conObj = act.filter(c => c.dias_obj != null)
  const Nobj = conObj.reduce((a, c) => a + Number(c.cantidad), 0)
  const diasObj = Nobj ? conObj.reduce((a, c) => a + c.dias_obj * Number(c.cantidad), 0) / Nobj : null
  const alojados = (raw?.grupos || []).filter(group => group.estado === 'alojado' && Number(group.animales) > 0)
  const animales = raw?.penWeights && alojados.length
    ? alojados.reduce((sum, group) => sum + Number(group.animales), 0)
    : N
  return { animales, corralesActivos: act.length, pesoMedio, gmd, vspic, pesoIng, diasObj }
}
